import { EventBus } from "ts-bus";
import { createEventDefinition } from "ts-bus";
import { Feature } from 'ol';
import {  FeatureType, ICAMSKey, IMapInteractionConfig, INode } from '../Models/UtilityModels';
import { ActivityListRequestModel, ActivityCreateModel, Activity } from "../Activity/Activity";
import { ICAMSContact, ContactListConfig } from "../Contacts/ContactsModels";
import { ReportModel } from "../../Reports/ReportsModels";
import { MapLayerGroupType, INewGeometryConfig } from "../Models/MapConfigModels";
import { OptionalTracingLayer, exportMapToPngCallback, MainMap } from "../Map/MainMap";
import GeometryType from "ol/geom/GeometryType";
import { infraConditionCallback } from "../Infra/Entity_InfraCondition";
import { routeConditionCreateCallback, routeConditionEditCallback } from "../Route/Entity_RouteCondition";
import { tasksCompletePanelCallback, Tasks } from "../Tasks/Tasks";
import { TaskAssignOptions } from "../Tasks/Tasks_Assign";
import * as events from '../Bus/Bus';
import { Utilities } from "../Utility/Utilities";
import { Audit } from "../Audit/Audit";
import { Maint } from "../Maint/Entity_Maint";
import { Routes } from "../Route/Entity_Routes";
import { Infra } from "../Infra/Entity_Infra";
import { Land } from "../Land/Entity_Land";
import { TaskManagementRecordList } from "../../TaskManagement/TaskManagementRecordList";
import { Annotations } from "../../Annotations/Annotations";
import { LegalEvents } from "../../Legal/LegalEvents";
import { ConditionSurvey } from "../../ConditionSurvey/ConditionSurvey";
import { Consultations } from "../../Consultations/Consultations";
import { Contacts } from "../Contacts/Contacts";
import { Reports } from "../../Reports/Reports";
import { OfficerMapMapSelect } from '../../OfficerMap/OfficerMapMapSelect';
import { MapDrawGeometry } from '../Map/MapDrawGeometry';
import { SelectList } from "../SelectList/SelectList";
import { MyTasksRecordList } from "../../MyTasks/MyTasksRecordList";
import { Path } from '../Path/Entity_Path';
import { TRO } from "../../TRO/TRO";
import { Photos } from "../Photos/Photos";
import { OfficerMapRecordList } from "../../OfficerMap/OfficerMapRecordList";
import { Notification } from '../Notification/Notification';

// CREATE THE EVENT BUS
// This is a singleton-pattern and stores a single instance of the bus on window
declare global {
    interface Window {
        __eventBus__: EventBus;
    }
}
export abstract class Bus {
    public static GetBus(): EventBus {
        if (window.__eventBus__ == null) {
            window.__eventBus__ = new EventBus();
        }
        return window.__eventBus__;
    }
}
export const bus = Bus.GetBus();

// ----------------------------------------------------------- //
// GLOBAL EVENTS

// MAIN PAGE
export const eventResetAllPanels = createEventDefinition()("main.eventResetAllPanels");
export const eventResetRightPanels = createEventDefinition()("main.eventResetRightPanels");
export const eventResetAllButtons = createEventDefinition<boolean>()("main.resetAllButtons");
export const eventResetLeftWizardPanels = createEventDefinition()("main.resetLeftWizardPanels");
export const eventMainPageLoadComplete = createEventDefinition()("main.mainPageLoadComplete");

// ACTIVITY
export const eventShowActivityList = createEventDefinition<[any, ActivityListRequestModel, any]>()("activity.showActivityList");
export const eventOpenActivityCreatePanel = createEventDefinition<[ActivityCreateModel, Function]>()("activity.openActivityCreatePanel");
export const eventOpenActivityDetailsPanel = createEventDefinition<[number, Function]>()("activity.openActivityDetailsPanel");
export const eventGetActivities = createEventDefinition<[ICAMSKey, Boolean, Function]>()("activity.eventGetActivities");

// AUDIT
export const eventOpenAuditPopover = createEventDefinition<[any, ICAMSKey]>()("audit.openAuditPopover");

// MAP
export const eventMultipleFeaturesSelected = createEventDefinition<{ features: Array<Feature>, point: any }>()("map.multipleFeaturesSelected");
export const eventDrawCompelted = createEventDefinition<Feature>()("map.drawCompelted");
export const eventSingleFeatureSelected = createEventDefinition<Feature>()("map.singleFeatureSelected");
export const eventRouteSelected = createEventDefinition<Feature>()("map.routeSelected");
export const eventRectangleSelectionComplete = createEventDefinition<Feature>()("map.rectangleSelectionComplete");
export const eventUnselectedFeatures = createEventDefinition<{ keys: Array<ICAMSKey> }>()("map.unselectedFeatures");
export const eventZoomToAnyFeature = createEventDefinition<[ICAMSKey, boolean, Function]>()("map.zoomToAnyFeature");
export const eventClearAllHighlights = createEventDefinition()("map.clearAllHighlights");
export const eventClearEditLayer = createEventDefinition()("map.clearEditLayer");
export const eventGetSelectedFeatures = createEventDefinition<Function>()("map.getSelectedFeatures");
export const eventClearSelectedFeatures = createEventDefinition()("map.clearSelectedFeatures");
export const eventZoomToWKTandHighlight = createEventDefinition<[Array<string>, boolean, boolean, FeatureType]>()("map.zoomToWKTandHighlight");
export const eventZoomToLatLon = createEventDefinition<[ICAMSKey, boolean, boolean]>()("map.zoomToLatLon");
export const eventZoomToGridRef = createEventDefinition<[any, boolean, boolean]>()("map.zoomToGridRef");
export const eventZoomToLocationAndThenHighlightFeature = createEventDefinition<[ICAMSKey, boolean, Function]>()("map.zoomToLocationAndThenHighlightFeature");
export const eventModifyFeature = createEventDefinition<[ICAMSKey, boolean]>()("map.modifyFeature");
export const eventModifyFeatureSave = createEventDefinition<Function>()("map.modifyFeatureSave");
export const eventModifyFeatureCancel = createEventDefinition()("map.modifyFeatureCancel");
export const eventMapGeometrySaved = createEventDefinition<ICAMSKey>()("map.mapGeometrySaved");
export const eventStartRouteSelection = createEventDefinition<IMapInteractionConfig>()("map.startRouteSelection");
export const eventStartDrawSelection = createEventDefinition<[GeometryType, number, Feature, number, OptionalTracingLayer]>()("map.startDrawSelection");
export const eventStartSingleSelection = createEventDefinition<IMapInteractionConfig>()("map.startSingleSelection");
export const eventStartMultiSelection = createEventDefinition<IMapInteractionConfig>()("map.startMultiSelection");
export const eventRemoveAllMapInteractions = createEventDefinition()("map.removeAllMapInteractions");
export const eventRefreshMapLayer = createEventDefinition<FeatureType>()("map.refreshMapLayer");
export const eventSetVisibleMapLayer = createEventDefinition<[FeatureType, boolean]>()("map.setVisibleMapLayer");
export const eventSetAnnotationFilterUID = createEventDefinition<number>()("map.setAnnotationFilterUID");
export const eventExportMapToPng = createEventDefinition<[JQuery<HTMLElement>, exportMapToPngCallback]>()("map.exportMapToPng");
export const eventMapLayerVisibilitySwitch = createEventDefinition<[MapLayerGroupType, Boolean]>()("map.mapLayerVisibilitySwitch");
export const eventMapClosePopup = createEventDefinition()("map.mapClosePopup");
export const eventAddWKTtoEditLayer = createEventDefinition<[string, Boolean, Array<GeometryType>, Function]>()("map.addWKTtoEditLayer");
export const eventStartAdditionalLayersLocationInfo = createEventDefinition<any>()("map.startAdditionalLayersLocationInfo");


// MAP DRAW GEOMETRY
export const eventNewMapGeometryStart = createEventDefinition<[INewGeometryConfig, Function]>()("map.newMapGeometryStart");
export const eventNewMapGeometryCompleted = createEventDefinition<[Boolean, ICAMSKey]>()("map.newMapGeometryCompleted");
export const eventNewMapGeometryRestart = createEventDefinition<Function>()("map.newMapGeometryRestart");
export const eventNewMapGeometryClose = createEventDefinition<Function>()("map.newMapGeometryClose");
export const eventNewMapGeometryReDraw = createEventDefinition<[ICAMSKey, Feature]>()("map.newMapGeometryReDraw");

// SELECTLIST
export const eventOpenDetailsPanel = createEventDefinition<[ICAMSKey, string]>()("selectlist.openDetailsPanel");
export const eventOpenEditPanel = createEventDefinition<ICAMSKey>()("selectlist.openEditPanel");

// OFFICERMAP
export const eventGetMapSelectRecords = createEventDefinition<Function>()("officermap.getMapSelectRecords");
export const eventGetRecordSelectRecords = createEventDefinition<Function>()("officermap.getRecordSelectRecords");

// MAINT FORMS
export const eventOpenMaintCreatePanel = createEventDefinition<[ICAMSKey, Function]>()("maint.openMaintCreatePanel");
export const eventOpenMaintEditPanel = createEventDefinition<ICAMSKey>()("maint.openMaintEditPanel");
export const eventOpenMaintRejectPanel = createEventDefinition<[ICAMSKey, Function]>()("maint.openMaintRejectPanel");
export const eventNewMaintWizardStart = createEventDefinition<[ICAMSKey, Feature, Function]>()("maint.newMaintWizardStart");
export const eventGetIssueAndZoomToLocation = createEventDefinition<ICAMSKey>()("maint.getIssueAndZoomToLocation");
export const eventOpenMaintCommentCreatePanel = createEventDefinition<ICAMSKey>()("maint.openMaintCommentCreatePanel");
export const eventDeleteMaint = createEventDefinition<ICAMSKey>()("maint.deleteMaint");

// INFRA FORMS
export const eventOpenInfraEditPanel = createEventDefinition<[ICAMSKey, Function]>()("infra.openInfraEditPanel");
export const eventOpenInfraConditionPanel = createEventDefinition<[ICAMSKey, string, infraConditionCallback]>()("infra.openInfraConditionPanel");
export const eventNewInfraWizardStart = createEventDefinition<[ICAMSKey, Feature, Function]>()("infra.newInfraWizardStart");
export const eventGetInfraAndZoomToLocation = createEventDefinition<ICAMSKey>()("infra.getInfraAndZoomToLocation");
export const eventDeleteInfra = createEventDefinition<[ICAMSKey, Function]>()("infra.deleteInfra");
export const eventOpenInfraHistoricDetailsPanel = createEventDefinition<[ICAMSKey, Function]>()("infra.openInfraHistoricDetailsPanel");

// ROUTE FORMS
export const eventOpenRouteEditPanel = createEventDefinition<ICAMSKey>()("route.openRouteEditPanel");
export const eventOpenRouteConditionCreatePanel = createEventDefinition<[ICAMSKey, routeConditionCreateCallback]>()("route.openRouteConditionCreatePanel");
export const eventNewRouteWizardStart = createEventDefinition<[ICAMSKey, Boolean]>()("route.newRouteWizardStart");
export const eventGetRouteAndZoomToLocation = createEventDefinition<ICAMSKey>()("route.getRouteAndZoomToLocation");
export const eventDeleteRoute = createEventDefinition<ICAMSKey>()("maint.deleteRoute");

// LAND FORMS
export const eventOpenLandEditPanel = createEventDefinition<ICAMSKey>()("land.openLandEditPanel");
export const eventNewLandWizardStart = createEventDefinition<ICAMSKey>()("land.newLandWizardStart");
export const eventGetLandAndZoomToLocation = createEventDefinition<ICAMSKey>()("land.getLandAndZoomToLocation");
export const eventDeleteLand = createEventDefinition<ICAMSKey>()("maint.deleteLand");

// PATH FORMS
export const eventOpenPathEditPanel = createEventDefinition<ICAMSKey>()("path.openPathEditPanel");
export const eventOpenPathDetailsPanel = createEventDefinition<ICAMSKey>()("path.openPathDetailsPanel");

// TREE
export const eventAddToSelection = createEventDefinition<any>()("tree.addToSelection");
export const eventTreeStartMultiAndRectangleMapSelected = createEventDefinition<IMapInteractionConfig>()("tree.treeStartMultiAndRectangleMapSelected");

// MY Tasks
export const eventOpenMyTasksRecordListPanel = createEventDefinition()("mytasks.openMyTasksRecordListPanel");

// TASKS
export const eventOpenTasksViewPanel = createEventDefinition<[ICAMSKey, string, Function, Boolean]>()("tasks.openTasksViewPanel");
export const eventOpenTasksCreatePanel = createEventDefinition<[Array<ICAMSKey>, Function]>()("tasks.openTasksCreatePanel");
export const eventOpenTasksEditPanel = createEventDefinition<[ICAMSKey, Function]>()("tasks.openTasksEditPanel");
export const eventOpenTaskGroupViewPanel = createEventDefinition<[ICAMSKey, string, Function]>()("tasks.openTaskGroupViewPanel");
export const eventOpenTaskGroupCreatePanel = createEventDefinition<Array<string>>()("tasks.openTaskGroupCreatePanel");
export const eventOpenTaskGroupEditPanel = createEventDefinition<ICAMSKey>()("tasks.openTaskGroupEditPanel");
export const eventOpenTasksAcceptPanel = createEventDefinition<ICAMSKey>()("tasks.openTasksAcceptPanel");
export const eventOpenTasksAssignPanel = createEventDefinition<[Array<ICAMSKey>, TaskAssignOptions, Function]>()("tasks.openTasksAssignPanel");
export const eventOpenTasksRejectPanel = createEventDefinition<ICAMSKey>()("tasks.openTasksRejectPanel");
export const eventOpenTasksCompletePanel = createEventDefinition<[ICAMSKey, Boolean, tasksCompletePanelCallback]>()("tasks.openTasksCompletePanel");
export const eventOpenTaskGroupsCompletePanel = createEventDefinition<ICAMSKey>()("tasks.openTaskGroupsCompletePanel");
export const eventOpenTasksPoolAddPanel = createEventDefinition<Array<ICAMSKey>>()("tasks.openTasksPoolAddPanel");
export const eventOpenTaskGroupTasksPanel = createEventDefinition<Array<ICAMSKey>>()("tasks.openTaskGroupTasksPanel");
export const eventOpenTaskUnGroupTasksPanel = createEventDefinition<Array<ICAMSKey>>()("tasks.openTaskUnGroupTasksPanel");
export const eventOpenTaskReGroupTasksPanel = createEventDefinition<Array<ICAMSKey>>()("tasks.openTaskReGroupTasksPanel");
export const eventOpenTaskSignOffPanel = createEventDefinition<ICAMSKey>()("tasks.openTaskSignOffPanel");
export const eventOpenTaskInspectPanel = createEventDefinition<ICAMSKey>()("tasks.openTaskInspectPanel");
export const eventOpenAssignedTasksForm = createEventDefinition<[string, Function]>()("tasks.openAssignedTasksForm");
export const eventTaskUpdated = createEventDefinition<ICAMSKey>()("tasks.taskUpdated");
export const eventOpenOtherStatusTasksPanel = createEventDefinition<ICAMSKey>()("tasks.openOtherStatusTasksPanel");
export const eventDeleteTask = createEventDefinition<[ICAMSKey, Function]>()("tasks.deleteTask");

// PHOTO
export const eventPhotoShowModal = createEventDefinition<string>()("tasks.photoShowModal");
export const eventPhotoCreatePhotoThumbnail = createEventDefinition<[HTMLElement, Function]>()("tasks.photoCreatePhotoThumbnail");

// CONTACTS
export const eventOpenContactsListView = createEventDefinition<[boolean, ContactListConfig, Function]>()("contacts.openContactsListView");
export const eventOpenContactsCreatePanel = createEventDefinition()("contacts.openContactsCreatePanel");
export const eventOpenContactsEditPanel = createEventDefinition<[ICAMSKey, Boolean, Function ]>()("contacts.openContactsEditPanel");
export const eventOpenContactsAccountPanel = createEventDefinition<ICAMSKey>()("contacts.openContactsAccountPanel");
export const eventContactSelected = createEventDefinition<Array<ICAMSContact>>()("contacts.contactSelected");
export const eventContactsModalClosed = createEventDefinition<string>()("contacts.contactsModalClosed");

// NOTIFICATION
export const eventOpenNotificationQueryPanel = createEventDefinition<[ICAMSKey, Function]>()("notification.openNotificationQueryPanel");

// MISC
export const eventEntityUpdated = createEventDefinition<ICAMSKey>()("misc.entityUpdated");

// LEGAL
export const eventOpenLegalEventsEditPanel = createEventDefinition<ICAMSKey>()("legal.openLegalEventsEditPanel");
export const eventOpenLegalEventsEditPanelViaLegalFeature = createEventDefinition<string>()("legal.openLegalEventsEditPanelViaLegalFeature");

// CONSULTATION
export const eventOpenLegalConsultationDetailsPanel = createEventDefinition<ICAMSKey>()("legal.openLegalConsultationDetailsPanel");

// ANNOTATION
export const eventCreateAnnotationSet = createEventDefinition<ICAMSKey>()("annotation.createAnnotationSet");
export const eventSetCurrentAnnotation = createEventDefinition<[number, string, string, boolean, boolean]>()("annotation.setCurrentAnnotation");
export const eventShowAnnotationsForFeature = createEventDefinition<[ICAMSKey, boolean]>()("annotation.showAnnotationsForFeature");
export const eventAnnotationEditGeometryEventStart = createEventDefinition<number>()("annotation.annotationEditGeometryEventStart");
export const eventAnnotationEditTextEventStart = createEventDefinition<number>()("annotation.annotationEditTextEventStart");
export const eventAnnotationDeleteGeometryEventStart = createEventDefinition<number>()("annotation.annotationDeleteGeometryEventStart");

// REPORTS
export const eventOpenReportModal = createEventDefinition<ReportModel>()("report.openReportModal");
export const eventOpenReportOrSelectList = createEventDefinition<[ReportModel, string]>()("report.openReportOrSelectList");
export const eventShowMapReportRectangle = createEventDefinition<ReportModel>()("report.showMapReportRectangle");

// TRO
export const eventOpenTROEditPanel = createEventDefinition<ICAMSKey>()("tro.openTROEditPanel");
export const eventOpenTROGeomEditPanel = createEventDefinition<ICAMSKey>()("tro.openTROGeomEditPanel");
export const eventDeleteTROGeom = createEventDefinition<ICAMSKey>()("tro.deleteTROGeom");

// CONDITION SURVEY
export const eventOpenConditionSurveyStartPanel = createEventDefinition<ICAMSKey>()("conditionSurvey.openConditionSurveyStartPanel");
